import { useCallback, useEffect, useRef } from "react";
import { create } from "zustand";

import { Operation } from "src/api/flowVersionUpdateIndex";
import { NodeTypes } from "src/clients/flow-api";
import { NodeSuggestionType } from "src/copilot/NodeSuggestion";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { useSplitNodeSuggestion } from "src/nodeEditor/useSplitNodeSuggestion";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";

type Args = {
  nodeId: string;
  suggestion: NodeSuggestionType;
  completionId: string;
  conversationId: string;
};

type NodeSuggestionState = {
  suggestion: NodeSuggestionType | null;
};

type NodeSuggestionActions = {
  setSuggestion: (suggestion: NodeSuggestionType | null) => void;
};

type NodeSuggestionStore = ZustandStore<
  NodeSuggestionState,
  NodeSuggestionActions
>;

const useNodeSuggestionStore = create<NodeSuggestionStore>((set) => ({
  suggestion: null,
  actions: {
    setSuggestion: (suggestion) => set({ suggestion }),
  },
}));

export const useNodeSuggestionActions = () =>
  useNodeSuggestionStore((state) => state.actions);

export const useNodeSuggestion = () =>
  useNodeSuggestionStore((state) => state.suggestion);

export const useFormApplyNodeSuggestion = <T>(
  nodeId: string,
  applyFn: (meta: T) => void,
) => {
  const nodeSuggestion = useNodeSuggestion();
  const { setSuggestion } = useNodeSuggestionActions();

  const applyFnRef = useRef(applyFn);

  useEffect(() => {
    applyFnRef.current = applyFn;
  }, [applyFn]);

  useEffect(() => {
    if (!nodeSuggestion || !nodeId || nodeId !== nodeSuggestion.nodeId) {
      return;
    }

    applyFnRef.current(nodeSuggestion.meta as T);

    setSuggestion(null);
  }, [nodeSuggestion, nodeId, setSuggestion]);
};

export const useHandleApplyNodeSuggestion = () => {
  const { setSuggestion } = useNodeSuggestionActions();
  const graphStore = useGraphStore();
  const { updateNode } = graphStore;
  const { orgId, version } = useAuthoringContext();
  const {
    computeSplitNodeUpdates,
    showDeletionModal,
    isDeletionModalOpen,
    deletionInfo,
    handleConfirmDelete,
    handleCancelDelete,
  } = useSplitNodeSuggestion();

  const finishApplyingSuggestion = useCallback(
    (
      nodeId: string,
      suggestion: NodeSuggestionType,
      completionId: string,
      conversationId: string,
    ) => {
      updateNode({
        nodeId,
        newName: suggestion.name,
      });
      setSuggestion(suggestion);

      tracker.emit(
        trackingEvents.copilotApplySuggestion({
          organization_id: orgId,
          flow_version_id: version.id,
          etag: version.etag!,
          node_id: nodeId,
          conversation_id: conversationId,
          completion_id: completionId,
        }),
      );
    },
    [updateNode, setSuggestion, orgId, version],
  );

  const handleApply = useCallback(
    async ({ nodeId, suggestion, completionId, conversationId }: Args) => {
      // For split nodes, compute updates and handle branch deletion if needed
      if (suggestion.type === NodeTypes.SPLIT_2) {
        const updates = computeSplitNodeUpdates(nodeId, suggestion);

        if (updates) {
          const { nodesUpdate, edgesUpdate, groupsUpdate, operation } = updates;

          // If branches need to be deleted, show confirmation modal and wait for user choice
          if (operation === Operation.DELETE_BRANCH) {
            const userConfirmed = await showDeletionModal(updates);
            if (!userConfirmed) {
              return;
            }
          }

          try {
            if (
              !graphStore.versionId ||
              !graphStore.updateFlowVersionGraphAndStore
            ) {
              return;
            }

            await graphStore.updateFlowVersionGraphAndStore({
              flowVersionId: graphStore.versionId,
              nodes: nodesUpdate,
              edges: edgesUpdate,
              groups: groupsUpdate,
              operation,
            });
          } catch (error) {
            logger.error("Failed to apply suggestion update", error);
            return;
          }
        }
      }
      finishApplyingSuggestion(
        nodeId,
        suggestion,
        completionId,
        conversationId,
      );
    },
    [
      computeSplitNodeUpdates,
      showDeletionModal,
      graphStore,
      finishApplyingSuggestion,
    ],
  );

  return {
    handleApply,
    isDeletionModalOpen,
    deletionInfo,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
